import React, { useRef, useState } from "react";
import { UploadCloud, Download, Loader2, FileSpreadsheet, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { errorMessage } from "../../lib/api";

const TEMPLATE = `Name,Price,Category,Description,Veg,Available
Smoked Pork with Bamboo Shoot,220,Mains,Naga-style smoked pork,No,Yes
Veg Thukpa,140,Noodles,Hearty veg noodle soup,Yes,Yes
Filter Coffee,60,Beverages,,Yes,Yes`;

// Minimal CSV parser handling quoted fields, commas and escaped quotes.
function parseCSV(text) {
  const rows = [];
  let row = [], field = "", inQuotes = false;
  text = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') { if (text[i + 1] === '"') { field += '"'; i++; } else inQuotes = false; }
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else field += c;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}

const truthy = (v) => ["yes", "y", "true", "1", "veg", "available"].includes(String(v || "").trim().toLowerCase());
const header = (h) => String(h || "").trim().toLowerCase();

function toItems(rows) {
  if (rows.length < 2) return { items: [], errors: [{ line: 0, msg: "File has no data rows." }] };
  const cols = rows[0].map(header);
  const idx = (name) => cols.indexOf(name);
  const ci = { name: idx("name"), price: idx("price"), category: idx("category"), description: idx("description"), veg: idx("veg"), available: idx("available") };
  if (ci.name === -1 || ci.price === -1) return { items: [], errors: [{ line: 1, msg: "Header must include at least Name and Price columns." }] };
  const items = [], errors = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    const name = (cells[ci.name] || "").trim();
    const priceRaw = (cells[ci.price] || "").trim();
    const price = Number(priceRaw);
    if (!name) { errors.push({ line: r + 1, msg: "Missing name" }); continue; }
    if (!priceRaw || isNaN(price) || price <= 0) { errors.push({ line: r + 1, msg: `Invalid price "${priceRaw}" for ${name}` }); continue; }
    items.push({
      name,
      price: Math.round(price * 100) / 100,
      category: (ci.category !== -1 && (cells[ci.category] || "").trim()) || "Mains",
      description: ci.description !== -1 ? ((cells[ci.description] || "").trim() || null) : null,
      veg: ci.veg === -1 ? true : truthy(cells[ci.veg]),
      is_available: ci.available === -1 ? true : truthy(cells[ci.available]),
      out_of_stock: false,
    });
  }
  return { items, errors };
}

export default function BulkMenuImport({ open, onOpenChange, onImport, onDone }) {
  const fileRef = useRef(null);
  const [parsed, setParsed] = useState(null); // { items, errors, fileName }
  const [busy, setBusy] = useState(false);

  const reset = () => { setParsed(null); if (fileRef.current) fileRef.current.value = ""; };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "menu-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const { items, errors } = toItems(parseCSV(text));
      setParsed({ items, errors, fileName: file.name });
      if (!items.length) toast.error("No valid rows found — check the columns and try again.");
    } catch (err) { toast.error("Could not read the file"); }
  };

  const confirm = async () => {
    if (!parsed?.items?.length) return;
    setBusy(true);
    try {
      const res = await onImport(parsed.items);
      toast.success(`Imported ${res?.added ?? parsed.items.length} item${(res?.added ?? parsed.items.length) === 1 ? "" : "s"}`);
      onDone?.();
      onOpenChange(false);
      reset();
    } catch (err) { toast.error(errorMessage(err, "Import failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import menu</DialogTitle>
          <DialogDescription>Upload a CSV with columns: Name, Price, Category, Description, Veg, Available. Items are added to the existing menu.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <button onClick={downloadTemplate} className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:underline" data-testid="bulk-download-template">
            <Download className="h-4 w-4" /> Download CSV template
          </button>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-surface-300 bg-surface-50 px-4 py-8 text-center hover:border-brand" data-testid="bulk-file-label">
            <UploadCloud className="h-7 w-7 text-ink-muted" />
            <span className="text-sm font-medium text-ink">{parsed?.fileName || "Choose a CSV file to upload"}</span>
            <span className="text-xs text-ink-muted">.csv exported from Excel, Google Sheets or Numbers</span>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="bulk-file-input" />
          </label>

          {parsed && (
            <div className="space-y-3" data-testid="bulk-preview">
              <div className="flex flex-wrap gap-3 text-sm">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 font-semibold text-green-700" data-testid="bulk-valid-count"><CheckCircle2 className="h-4 w-4" /> {parsed.items.length} ready to import</span>
                {parsed.errors.length > 0 && <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-700" data-testid="bulk-error-count"><AlertTriangle className="h-4 w-4" /> {parsed.errors.length} skipped</span>}
              </div>
              {parsed.items.length > 0 && (
                <div className="max-h-56 overflow-y-auto rounded-xl border border-surface-200">
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-surface-100 text-ink-soft"><tr><th className="px-3 py-2">Name</th><th className="px-3 py-2">Price</th><th className="px-3 py-2">Category</th><th className="px-3 py-2">Veg</th></tr></thead>
                    <tbody>
                      {parsed.items.slice(0, 50).map((it, i) => (
                        <tr key={i} className="border-t border-surface-200" data-testid={`bulk-preview-row-${i}`}>
                          <td className="px-3 py-1.5 font-medium text-ink">{it.name}</td>
                          <td className="px-3 py-1.5">₹{it.price}</td>
                          <td className="px-3 py-1.5">{it.category}</td>
                          <td className="px-3 py-1.5">{it.veg ? "Veg" : "Non-veg"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {parsed.errors.length > 0 && (
                <div className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800">
                  <p className="font-semibold">Skipped rows:</p>
                  <ul className="mt-1 list-disc space-y-0.5 pl-4">{parsed.errors.slice(0, 8).map((e, i) => <li key={i}>Line {e.line}: {e.msg}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          <Button onClick={confirm} disabled={busy || !parsed?.items?.length} className="h-11 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="bulk-confirm">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} Import {parsed?.items?.length || ""} item{parsed?.items?.length === 1 ? "" : "s"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
